import { ReactComponent as PauseIcon } from "shared/assets/Icons/pause.svg";
import { ReactComponent as PlayIcon } from "shared/assets/Icons/play.svg";
import Spinner from "@brighthr/component-spinner";
import { useTranslation } from "react-i18next";
import { formatDuration } from "shared/utils/formatDuration";

type AudioPlayerControlBarProps = {
  disabled?: boolean;
  position: number;
  duration: number;
  isPlaying: boolean;
  isLoading?: boolean;
  play: () => void;
  pause: () => void;
  onSeekMouseDown: () => void;
  onSeekMouseUp: () => void;
  onSeekChange: (position: number) => void;
  playLabel?: string;
  pauseLabel?: string;
};

export const AudioPlayerControlBar = ({
  isPlaying,
  duration,
  position,
  isLoading = false,
  pause,
  play,
  onSeekMouseDown,
  onSeekMouseUp,
  onSeekChange,
  disabled = false,
  playLabel,
  pauseLabel,
}: AudioPlayerControlBarProps) => {
  const { t } = useTranslation();
  const timeRemaining = Math.min(duration * position, duration);

  return (
    <>
      <input
        disabled={disabled}
        type="range"
        className="accent-brand h-10 w-full"
        aria-label={t("resourceViewer.player.timeSlider")}
        value={position}
        min={0}
        max={1}
        step="any"
        onMouseDown={() => onSeekMouseDown()}
        onMouseUp={() => onSeekMouseUp()}
        onTouchStart={() => onSeekMouseDown()}
        onTouchEnd={() => onSeekMouseUp()}
        onChange={(e) => {
          onSeekChange(parseFloat(e.currentTarget.value));
        }}
      />
      <div className="flex flex-row items-center">
        <button
          disabled={disabled}
          className="bg-navy-blue hover:bg-navy-blue-light text-white rounded-full px-10 py-4 mr-4 shadow-md"
          aria-label={isPlaying ? pauseLabel ?? t("resourceViewer.player.pauseLabel") : playLabel ?? t("resourceViewer.player.playLabel")}
          onClick={() => (isPlaying ? pause() : play())}
        >
          {isPlaying ? <PauseIcon className="w-8 h-8" /> : <PlayIcon className="w-8 h-8" />}
        </button>
        {isLoading && <Spinner ariaLabel={t("buttons.loading")} className="stroke-brand-purple" size="xs" />}
        {!isLoading && (
          <>
            <span className="mr-1" aria-label={t("resourceViewer.player.timeRemaining")}>
              {formatDuration(timeRemaining)}
            </span>
            /
            <span className="ml-1" aria-label={t("resourceViewer.player.duration")}>
              {formatDuration(duration)}
            </span>
          </>
        )}
      </div>
    </>
  );
};
